// CreateNewSection.jsx

import React, { useEffect, useMemo, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';
import { useBase } from 'context/BaseContext';
import { useNodeTypes } from 'context/NodeTypesContext';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Utilities
import { isAppProvisionedType } from 'utilities/baseDatabaseDisplay';
import { buildLibraryCaptureActions } from 'main/sidebar/buckets/library/buildLibraryCaptureActions';
import {
  typeAllowsManualCreate,
  typeAllowsTopLevelCreate,
} from 'utilities/typeCreatePolicy';
import { expandAddMenuRows, typeHasAddMenuPresence } from 'utilities/typeAppearPolicy';

// Services
import { subscribeToBaseAppInstances } from 'services/appInstances/AppInstanceReadService';
import { subscribeToCollectionsShared } from 'services/collections';

// CSS
import './CreateNewSection.css';

// Icons
import Icon from 'svg/Icon';

// Components
import FindExistingSection from 'components/addmodal/sections/find/FindExistingSection';
import Divider from 'components/divider/Divider';
import TextInput from 'components/textinput/TextInput';
import { BASE_LAUNCHER_DEFS, isBaseLauncherKey } from 'components/addmodal/utilities/baseLaunchers';
import {
  MEDIA_ACTION_DEFS,
} from 'components/addmodal/utilities/mediaActions';

/**
 * stripCaptureAddPrefix
 *
 * Turns sidebar Capture labels ("Add Note") into AddModal labels ("Note").
 *
 * @param {string} label - Capture action label.
 * @returns {string}
 */
function stripCaptureAddPrefix(label) {
  return String(label || '').replace(/^Add\s+/i, '').trim();
}

const getTypeActionLabel = (typeDef) => (
  String(typeDef?.label || typeDef?.key || '').trim()
);
/**
 * CreateNewSection
 *
 * Renders the UI for creating a new item in the Add Modal (node types and optional
 * base launchers).
 *
 * @param {array} availableTypes - Available node type definitions that can be created.
 * @param {function} onTypeSelect - Handler function to call when a node type is selected for creation.
 * @param {function} onMoreClick - Handler function to open the secondary "More" panel.
 * @param {function} onClose - Handler function to call when the section should signal that it can be closed (e.g. after selecting a type).
 * @param {boolean} showSearchInput - Whether to display the search input.
 * @param {boolean} showBaseLaunchers - Whether to show Database/Channel/Message/Dashboard launchers.
 * @param {boolean} showMediaActions - Whether to show consolidated Camera / Files actions.
 * @param {boolean} libraryInfoMode - Project Information “Add More Info” Capture picker (no base launchers).
 * @param {string} searchTerm - Current search text value.
 * @param {function} setSearchTerm - Setter for search term updates.
 * @param {string} searchError - Search error message.
 * @param {boolean} searching - Whether existing-search is in progress.
 * @param {boolean} noResults - Whether no results were found.
 * @param {array} searchResults - Existing-search result rows.
 * @param {object} availableTypeMap - Available type map used by existing-search rendering.
 * @param {string} anchorNodeKey - Anchor node key for attach operations.
 * @param {string} attachingNodeKey - Attaching node key for attach operations.
 * @param {function} onAttachExisting - Handler for attaching an existing node.
 *
 * @returns {ReactNode} The rendered component.
 */
const CreateNewSection = React.memo(({
  availableTypes,
  enableSelectionWithoutAnchor = false,
  onTypeSelect,
  onMoreClick,
  onClose,
  showMoreCard = true,
  showBaseLaunchers = false,
  showMediaActions = true,
  libraryInfoMode = false,
  showSearchInput = false,
  searchTerm = '',
  setSearchTerm,
  searchError,
  searching,
  noResults,
  searchResults,
  availableTypeMap,
  anchorNodeKey,
  attachingNodeKey,
  onAttachExisting,
  relatedTypeSelectMode = false,
}) => {

  // ----- CONTEXT ----- //

  // Theme Context

  const { theme } = useTheme();

  // Base Context
  const { activeBase } = useBase();

  // Node Types Context
  const { getType } = useNodeTypes();

  // ----- DEVICE DETECTION ----- //

  const isMobile = useIsMobile();

  // ----- STATE ----- //

  // Hovered type card key.

  const [hoveredTypeKey, setHoveredTypeKey] = useState('');

  // App instances + collections for Project Information Capture picker.
  const [appInstances, setAppInstances] = useState([]);
  const [libraryDatabases, setLibraryDatabases] = useState([]);

  // ----- EFFECTS ----- //

  useEffect(() => {
    if (!libraryInfoMode) {
      setAppInstances([]);
      setLibraryDatabases([]);
      return undefined;
    }

    const baseKey = String(activeBase?.key || '').trim();
    if (!baseKey) {
      setAppInstances([]);
      setLibraryDatabases([]);
      return undefined;
    }

    const unsubApps = subscribeToBaseAppInstances(baseKey, (rows) => {
      setAppInstances(Array.isArray(rows) ? rows : []);
    });
    const unsubCollections = subscribeToCollectionsShared(baseKey, (rows) => {
      setLibraryDatabases(Array.isArray(rows) ? rows : []);
    });

    return () => {
      unsubApps?.();
      unsubCollections?.();
    };
  }, [activeBase?.key, libraryInfoMode]);

  // ----- MEMOS ----- //

  // Visible type definitions. Show in Add menu replaces the old hide list.
  // Global / Capture create pickers also respect manual + top-level create policy.
  // Related-type and library-info pickers keep child-only types available.

  const visibleTypes = useMemo(() => {
    const enforceTopLevelCreatePolicy = !relatedTypeSelectMode && !libraryInfoMode;
    return (availableTypes || []).filter((typeDef) => {
      if (!typeHasAddMenuPresence(typeDef)) return false;
      if (!enforceTopLevelCreatePolicy) return true;
      return typeAllowsManualCreate(typeDef) && typeAllowsTopLevelCreate(typeDef);
    });
  }, [availableTypes, libraryInfoMode, relatedTypeSelectMode]);

  // Alphabetized types by display action label.
  const sortedTypes = useMemo(() => {
    return expandAddMenuRows(visibleTypes).sort((a, b) => {
      const aLabel = getTypeActionLabel(a).toLowerCase();
      const bLabel = getTypeActionLabel(b).toLowerCase();
      return aLabel.localeCompare(bLabel);
    });
  }, [visibleTypes]);

  const libraryInfoDisplayTypes = useMemo(() => {
    if (!libraryInfoMode) return null;

    const model = buildLibraryCaptureActions({
      appInstances,
      libraryDatabases,
      getType,
      isAppProvisioned: isAppProvisionedType,
    });

    const toCard = (action) => ({
      key: String(action?.typeKey || action?.key || '').trim(),
      label: stripCaptureAddPrefix(action?.label),
      iconKey: action?.iconKey || 'deployed_code',
      description: '',
    });

    // Preserve Capture order: system → pack → custom (do not re-alphabetize).
    return [
      ...model.system.map(toCard),
      ...model.pack.map(toCard),
      ...model.custom.map(toCard),
    ].filter((row) => row.key && row.label);
  }, [appInstances, getType, libraryDatabases, libraryInfoMode]);

  const displayTypes = useMemo(() => {
    if (libraryInfoDisplayTypes) return libraryInfoDisplayTypes;

    let rows = sortedTypes;

    if (showMediaActions) {
      rows = [...rows, ...MEDIA_ACTION_DEFS];
    }

    if (showBaseLaunchers) {
      rows = [...rows, ...BASE_LAUNCHER_DEFS];
    }

    return [...rows].sort((a, b) => {
      const aLabel = getTypeActionLabel(a).toLowerCase();
      const bLabel = getTypeActionLabel(b).toLowerCase();
      return aLabel.localeCompare(bLabel);
    });
  }, [
    libraryInfoDisplayTypes,
    showMediaActions,
    showBaseLaunchers,
    sortedTypes,
  ]);

  const includeMoreAction = showMoreCard && !showBaseLaunchers && !libraryInfoMode;

  // Whether search mode should render existing-node results.
  const hasSearchQuery = String(searchTerm || '').trim().length > 0;

  const searchPlaceholder = useMemo(() => {
    const baseTitle = String(activeBase?.title || '').trim() || 'this base';
    return `Search ${baseTitle}…`;
  }, [activeBase?.title]);

  // ----- HANDLERS ----- //

  /**
   * Handles selecting a type key for creation.
   *
   * @param {string} typeKey - Type key to create.
   */

  const handleTypeSelect = (typeKey, options = {}) => {
    if (typeof onTypeSelect === 'function') onTypeSelect(typeKey, options);
    // Keep AddModal open when launching nested create modals so dismiss returns here.
    if (!relatedTypeSelectMode && !isBaseLauncherKey(typeKey)) onClose?.();
  };

  // ----- UTILITIES ----- //

  /**
   * Renders the icon for a type definition or fallback action.
   *
   * @param {Object} options - Render options.
   * @param {string} options.iconKey - Material icon key.
   * @param {number} options.size - Icon size.
   *
   * @returns {ReactNode} Icon markup.
   */
  const renderTypeIcon = ({ iconKey, size }) => (
    <Icon
      iconKey={iconKey || 'deployed_code'}
      width={size}
      height={size}
      color={theme.foreground}
    />
  );

  /**
   * Renders the card grid for a list of type definitions.
   *
   * @param {array} types - Type definitions to render.
   *
   * @returns {ReactNode} Grid markup for type cards.
   */
  const renderTypeGrid = (types, { includeMoreCard = false } = {}) => (

    <div className="create-new-section-grid">
      {types.map((typeDef) => {
        const iconKeyValue = String(typeDef.iconKey || '').trim() || 'deployed_code';
        const actionLabel = getTypeActionLabel(typeDef);
        const cardKey = String(typeDef.menuKey || typeDef.key || '').trim();

        return (
          <div
            className="create-new-section-card"
            style={{
              backgroundColor: hoveredTypeKey === cardKey ? theme.backgroundHover : theme.backgroundMuted,
              borderColor: hoveredTypeKey === cardKey ? theme.foregroundMuted : 'transparent',
            }}
            key={cardKey}
            onClick={() => handleTypeSelect(typeDef.key, {
              appearAsKey: typeDef.appearAsKey,
              initialFields: typeDef.initialFields,
            })}
            onMouseEnter={() => setHoveredTypeKey(cardKey)}
            onMouseLeave={() => setHoveredTypeKey('')}
          >
            {renderTypeIcon({
              iconKey: iconKeyValue,
              size: 40,
            })}

            {/* CARD TEXT */}
            <div className="create-new-section-card-text">

              {/* CARD LABEL */}
              <div
                className="create-new-section-card-label"
              >
                {actionLabel}
              </div>
              {/**
              {actionDescription ? (
                <div

                  className="create-new-section-card-description"
                  style={{ color: theme.foregroundMuted }}
                >
                  {actionDescription}
                </div>
              ) : null}
                */}
            </div>
          </div>
        );
      })}

      {includeMoreCard && (
        <div
          className="create-new-section-card"
          style={{
            backgroundColor: hoveredTypeKey === 'more' ? theme.backgroundHover : theme.backgroundMuted,
            borderColor: hoveredTypeKey === 'more' ? theme.foregroundMuted : 'transparent',
          }}
          key="more"
          onClick={onMoreClick}
          onMouseEnter={() => setHoveredTypeKey('more')}
          onMouseLeave={() => setHoveredTypeKey('')}
        >
          {renderTypeIcon({ iconKey: 'more_horiz', size: 30 })}

          {/* CARD LABEL */}
          <div
            className="create-new-section-card-label"
          >
            More
          </div>
        </div>
      )}
    </div>
  );

  // ----- RENDER ----- //

  return (
    <div className="create-new-section-container">

      {showSearchInput && !relatedTypeSelectMode && (
        <>

          {/* HEADER */}
          <div
            className="create-new-section-header"
            style={{
              marginBottom: 4,
            }}
          >
            ADD EXISTING ITEM
          </div>

          {/* CONTROLS */}
          <div className="create-new-section-controls">
            <TextInput
              value={searchTerm}
              placeholder={searchPlaceholder}
              autoFocus={!isMobile}
              onChange={(event) => setSearchTerm?.(event.target.value)}
            />
          </div>

          <Divider margin={6} />
        </>
      )}

      {/* SCROLL AREA */}
      <div className="create-new-section-scroll-area">
        {hasSearchQuery ? (
          <FindExistingSection
            hideInput
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            searchError={searchError}
            searching={searching}
            noResults={noResults}
            searchResults={searchResults}
            availableTypeMap={availableTypeMap}
            anchorNodeKey={anchorNodeKey}
            attachingNodeKey={attachingNodeKey}
            enableSelectionWithoutAnchor={enableSelectionWithoutAnchor}
            onAttachExisting={onAttachExisting}
          />
        ) : (
          <>

                {/*  
            {!relatedTypeSelectMode && (
              <div
                className="create-new-section-header-row"
                style={{
                  marginBottom: 12,
                }}
              >

                <div
                  className="create-new-section-header"
                >
                  ADD NEW ITEM
                </div>

                <div className="create-new-section-view-actions">

                  <Button
                    iconKey="view_list"
                    iconSize={16}
                    padding="0px"
                    borderRadius="6px"
                    height="30px"
                    width="30px"
                    onClick={() => setViewMode(VIEW_MODES.LIST)}
                  />

                  <Button
                    iconKey="grid_view"
                    iconSize={16}
                    padding="0px"
                    borderRadius="6px"
                    height="30px"
                    width="30px"
                    onClick={() => setViewMode(VIEW_MODES.GRID)}
                  />

                </div>

              </div>
            )}

            {relatedTypeSelectMode || viewMode === VIEW_MODES.GRID
              ? renderTypeGrid(displayTypes, { includeMoreCard: includeMoreAction && !relatedTypeSelectMode })
              : renderTypeList(displayTypes, { includeMoreRow: includeMoreAction })}

              */}
              {renderTypeGrid(displayTypes, { includeMoreCard: includeMoreAction && !relatedTypeSelectMode })}
          </>
        )}
      </div>
    </div>
  );
});

// Dev Tools
CreateNewSection.displayName = 'CreateNewSection';

export default CreateNewSection;
